// src/services/storyService.js
import { db } from '../config/firebase';
import { 
  collection, doc, addDoc, updateDoc, deleteDoc, 
  getDocs, getDoc, query, where, orderBy, 
  serverTimestamp 
} from 'firebase/firestore';
import { firestoreService } from './firestoreService';

class StoryService {
  async createStory(bookId, storyData) {
    try {
      const storiesRef = collection(db, `pb-books/${bookId}/stories`);
      
      const docRef = await addDoc(storiesRef, {
        ...storyData,
        bookId,
        level: storyData.level || 'gallery',
        galleryId: storyData.galleryId || null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      return docRef.id;
    } catch (error) {
      console.error('Error creating story:', error);
      throw error;
    }
  }

  async updateStory(bookId, storyId, updates) {
    try {
      const storyRef = doc(db, `pb-books/${bookId}/stories`, storyId);
      
      await updateDoc(storyRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });

      return true;
    } catch (error) {
      console.error('Error updating story:', error);
      throw error;
    }
  }

  async getAlbumStories(bookId) {
    try {
      const storiesRef = collection(db, `pb-books/${bookId}/stories`);
      const q = query(storiesRef, orderBy('createdAt', 'desc'));
      
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting album stories:', error);
      return [];
    }
  }

  async getGalleryStories(bookId, galleryId) {
    try {
      const storiesRef = collection(db, `pb-books/${bookId}/stories`);
      const q = query(
        storiesRef, 
        where('level', '==', 'gallery'),
        where('galleryId', '==', galleryId),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting gallery stories:', error);
      return [];
    }
  }

  async getAlbumLevelStories(bookId) {
    try {
      const storiesRef = collection(db, `pb-books/${bookId}/stories`);
      const q = query(
        storiesRef, 
        where('level', '==', 'album'),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting album-level stories:', error);
      return [];
    }
  }

  async getStory(bookId, storyId) {
    try {
      const storyRef = doc(db, `pb-books/${bookId}/stories`, storyId);
      const storyDoc = await getDoc(storyRef);
      
      if (storyDoc.exists()) {
        return { id: storyDoc.id, ...storyDoc.data() };
      }
      
      return null;
    } catch (error) {
      console.error('Error getting story:', error);
      throw error;
    }
  }

  async deleteStory(bookId, storyId) {
    try {
      const storyRef = doc(db, `pb-books/${bookId}/stories`, storyId);
      await deleteDoc(storyRef);
      
      return true;
    } catch (error) {
      console.error('Error deleting story:', error);
      throw error;
    }
  }

  async uploadStoryAssets(bookId, file, assetType) {
    try {
      const path = `pb-${bookId}/stories/${assetType}`;
      return await firestoreService.uploadFile(file, path);
    } catch (error) {
      console.error('Error uploading story asset:', error);
      throw error;
    }
  }
}

export default new StoryService();